/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

"use client";

import React, { useState, useEffect, useMemo } from "react";
import { motion } from "motion/react";
import { ColumnDef } from "@tanstack/react-table";
import { SpeedTestResult, TimeRange } from "@/types/types";
import { SpeedHistoryChart } from "./SpeedHistoryChart";
import { MetricCard } from "@/components/common/MetricCard";
import { FeaturedMonitorWidget } from "@/components/monitor/FeaturedMonitorWidget";
import { ServerOverview } from "@/components/dashboard/ServerOverview";
import {
  FaWaveSquare,
  FaArrowDown,
  FaArrowUp,
  FaGripVertical,
} from "react-icons/fa";
import { IoIosPulse } from "react-icons/io";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { ShareIcon } from "@heroicons/react/24/outline";
import { ShareIcon as ShareIconSolid } from "@heroicons/react/24/solid";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { getSpeedTestColumns, getSpeedTestMobileColumns } from "./columns";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { formatDateTimeWithSettings, useTimeSettings } from "@/utils/timeSettings";

interface DashboardTabProps {
  latestTest: SpeedTestResult | null;
  tests: SpeedTestResult[];
  recentSpeedtestsRows?: number;
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
  isPublic?: boolean;
  hasAnyTests?: boolean;
  onShareClick?: () => void;
  onNavigateToSpeedTest?: () => void;
  onNavigateToVnstat?: (agentId?: number) => void;
}

interface DragHandleProps {
  dragHandleRef?: (node: HTMLElement | null) => void;
  dragHandleListeners?: Record<string, (...args: unknown[]) => unknown>;
  dragHandleClassName?: string;
}

interface SortableItemProps {
  id: string;
  children: React.ReactNode;
  dragHandleClassName?: string;
}

const SortableItem: React.FC<SortableItemProps> = ({
  id,
  children,
  dragHandleClassName = "drag-handle",
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    setActivatorNodeRef,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {React.cloneElement(children as React.ReactElement<DragHandleProps>, {
        dragHandleRef: setActivatorNodeRef,
        dragHandleListeners: listeners as any,
        dragHandleClassName,
      })}
    </div>
  );
};

// Wrapper component for SpeedHistoryChart with drag handle
interface DraggableSpeedHistoryChartProps {
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
  isPublic?: boolean;
  hasAnyTests?: boolean;
  hasCurrentRangeTests?: boolean;
  dragHandleRef?: (node: HTMLElement | null) => void;
  dragHandleListeners?: Record<string, (...args: unknown[]) => unknown>;
  dragHandleClassName?: string;
  // Server filtering props
  serverFilterMode: "all" | "single" | "multiple";
  selectedSingleServer: string;
  selectedMultipleServers: Set<string>;
  onServerFilterModeChange: (mode: "all" | "single" | "multiple") => void;
  onSelectedSingleServerChange: (server: string) => void;
  onSelectedMultipleServersChange: (servers: Set<string>) => void;
  // Multiple server display mode props
  multipleServerDisplayMode: "overlay" | "separate";
  onMultipleServerDisplayModeChange: (mode: "overlay" | "separate") => void;
}

const DraggableSpeedHistoryChart: React.FC<DraggableSpeedHistoryChartProps> = ({
  dragHandleRef,
  dragHandleListeners,
  dragHandleClassName,
  ...props
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
    >
      <SpeedHistoryChart
        {...props}
        showDragHandle={true}
        dragHandleRef={dragHandleRef}
        dragHandleListeners={dragHandleListeners}
        dragHandleClassName={dragHandleClassName}
      />
    </motion.div>
  );
};

export const DashboardTab: React.FC<DashboardTabProps> = ({
  latestTest,
  tests,
  recentSpeedtestsRows = 20,
  timeRange,
  onTimeRangeChange,
  isPublic = false,
  hasAnyTests = false,
  onShareClick,
  onNavigateToSpeedTest,
  onNavigateToVnstat,
}) => {
  const { settings } = useTimeSettings();
  const [displayCount, setDisplayCount] = useState(recentSpeedtestsRows);
  const [isRecentTestsOpen, setIsRecentTestsOpen] = useState(() => {
    const saved = localStorage.getItem("recent-tests-open");
    return saved === null ? true : saved === "true";
  });
  const columns = useMemo(() => getSpeedTestColumns(settings), [settings]);
  const mobileColumns = useMemo(
    () => getSpeedTestMobileColumns(settings),
    [settings]
  );

  // Initialize section order from localStorage or default
  const [sectionOrder, setSectionOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem("dashboard-section-order");
    return saved ? JSON.parse(saved) : ["history", "recent"];
  });

  // State for share button hover
  const [isShareHovered, setIsShareHovered] = useState(false);

  // Server filtering state
  const [serverFilterMode, setServerFilterMode] = useState<"all" | "single" | "multiple">("all");
  const [selectedSingleServer, setSelectedSingleServer] = useState<string>("all");
  const [selectedMultipleServers, setSelectedMultipleServers] = useState<Set<string>>(new Set());
  const [multipleServerDisplayMode, setMultipleServerDisplayMode] = useState<"overlay" | "separate">("overlay");

  // Initialize drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Persist recent tests open state to localStorage
  useEffect(() => {
    localStorage.setItem("recent-tests-open", isRecentTestsOpen.toString());
  }, [isRecentTestsOpen]);

  // Persist section order to localStorage
  useEffect(() => {
    localStorage.setItem(
      "dashboard-section-order",
      JSON.stringify(sectionOrder)
    );
  }, [sectionOrder]);

  useEffect(() => {
    setDisplayCount(recentSpeedtestsRows);
  }, [recentSpeedtestsRows]);

  const displayedTests = tests.slice(0, displayCount);

  // Apply server filtering to tests (same logic as in SpeedHistoryChart)
  const filteredDisplayTests = useMemo(() => {
    if (serverFilterMode === "single" && selectedSingleServer !== "all") {
      return tests.filter(test => test.serverName === selectedSingleServer);
    } else if (serverFilterMode === "multiple" && selectedMultipleServers.size > 0) {
      return tests.filter(test => selectedMultipleServers.has(test.serverName));
    }
    return tests;
  }, [tests, serverFilterMode, selectedSingleServer, selectedMultipleServers]);

  // Get the latest test from filtered results
  const filteredLatestTestComputed = useMemo(() => {
    return filteredDisplayTests.length > 0 ? filteredDisplayTests[0] : null;
  }, [filteredDisplayTests]);

  const calculateAverage = (field: keyof SpeedTestResult): string => {
    const dataToUse = filteredDisplayTests.length > 0 ? filteredDisplayTests : tests;
    if (dataToUse.length === 0) return "N/A";

    const validValues = dataToUse
      .map((test) => {
        const value = test[field];
        if (typeof value === "string") {
          return parseFloat(value.replace("ms", ""));
        }
        return Number(value);
      })
      .filter((value) => !isNaN(value));

    if (validValues.length === 0) return "N/A";

    const avg =
      validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
    return avg.toFixed(2);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSectionOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* All-metrics overview (Agents/Docker/Switches) -- shown above the
          speed test content so the dashboard leads with server health, not
          just network throughput. */}
      {!isPublic && <ServerOverview />}

      {/* No History Available message */}
      {!hasAnyTests && (
        <Card className="max-w-xl mx-auto p-4 sm:p-6">
          <div className="text-center space-y-3 sm:space-y-4">
            <div>
              <h2 className="text-gray-900 dark:text-white text-lg sm:text-xl font-semibold mb-1 sm:mb-2">
                No History Available
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
                Start monitoring your network performance
              </p>
            </div>

            {/* Empty Chart Visualization - more compact on mobile */}
            <div className="my-4 sm:my-6">
              <div className="flex items-end justify-center gap-1 sm:gap-2 h-16 sm:h-24">
                {[40, 60, 35, 70, 45, 55, 65].map((height, i) => (
                  <div
                    key={i}
                    className="w-6 sm:w-8 bg-gray-300/50 dark:bg-gray-700/50 rounded-t-sm transition-all duration-500"
                    style={{ height: `${height}%`, opacity: 0.3 + i * 0.1 }}
                  />
                ))}
              </div>
              <div className="border-t border-gray-300/50 dark:border-gray-700/50 mt-2" />
            </div>

            <div className="max-w-md mx-auto">
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Go to the{" "}
                <button
                  onClick={onNavigateToSpeedTest}
                  className="inline-flex items-center mx-1 px-3 py-2 sm:px-2 sm:py-1 min-h-[44px] sm:min-h-0 rounded-lg transition-colors text-xs bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 touch-manipulation"
                  disabled={!onNavigateToSpeedTest}
                >
                  Speed Test tab
                </button>{" "}
                to run manual tests or set up automated schedules
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Featured Vnstat Widget - only show if callback is provided and not public */}
      {onNavigateToVnstat && !isPublic && (
        <FeaturedMonitorWidget onNavigateToMonitor={onNavigateToVnstat} />
      )}

      {/* Latest Results */}
      {hasAnyTests && latestTest && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
          className="mb-6"
        >
          <h2
            className={cn(
              "text-gray-900 dark:text-white text-xl ml-1 font-semibold",
              isPublic && "sm:pt-6"
            )}
          >
            Latest Run
          </h2>
          <div className="flex justify-between ml-1 items-center text-gray-600 dark:text-gray-400 text-sm mb-4">
            <div>
              Last test run:{" "}
              {latestTest?.createdAt
                ? formatDateTimeWithSettings(latestTest.createdAt, settings)
                : "N/A"}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 cursor-default relative">
            <MetricCard
              icon={<IoIosPulse className="w-5 h-5 text-amber-500" />}
              title="Latency"
              value={parseFloat((filteredLatestTestComputed || latestTest)!.latency).toFixed(2)}
              unit="ms"
              average={calculateAverage("latency")}
            />
            <MetricCard
              icon={<FaArrowDown className="w-5 h-5 text-blue-500" />}
              title="Download"
              value={(filteredLatestTestComputed || latestTest)!.downloadSpeed.toFixed(2)}
              unit="Mbps"
              average={calculateAverage("downloadSpeed")}
            />
            <MetricCard
              icon={<FaArrowUp className="w-5 h-5 text-emerald-500" />}
              title="Upload"
              value={(filteredLatestTestComputed || latestTest)!.uploadSpeed.toFixed(2)}
              unit="Mbps"
              average={calculateAverage("uploadSpeed")}
            />
            <MetricCard
              icon={<FaWaveSquare className="w-5 h-5 text-purple-400" />}
              title="Jitter"
              value={(filteredLatestTestComputed || latestTest)!.jitter?.toFixed(2) ?? "N/A"}
              unit="ms"
              average={
                (filteredLatestTestComputed || latestTest)!.jitter !== null &&
                (filteredLatestTestComputed || latestTest)!.jitter !== undefined
                  ? calculateAverage("jitter")
                  : undefined
              }
            />

            {/* Floating Share Button positioned on the grid */}
            {!isPublic && onShareClick && (
              <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <motion.button
                      onClick={onShareClick}
                      onMouseEnter={() => setIsShareHovered(true)}
                      onMouseLeave={() => setIsShareHovered(false)}
                      className="relative p-2 min-w-[36px] min-h-[36px] text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200 z-10 opacity-60 hover:opacity-100 touch-manipulation flex items-center justify-center"
                      aria-label="Share public speed test page"
                      whileTap={{ scale: 0.9 }}
                      transition={{ duration: 0.2 }}
                    >
                      {isShareHovered ? (
                        <ShareIconSolid className="w-4 h-4" />
                      ) : (
                        <ShareIcon className="w-4 h-4" />
                      )}
                    </motion.button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Share speed test results</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Draggable Sections */}
      {hasAnyTests && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sectionOrder}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-6">
              {sectionOrder.map((sectionId) => {
                if (sectionId === "history") {
                  return (
                    <SortableItem key="history" id="history">
                      <DraggableSpeedHistoryChart
                        timeRange={timeRange}
                        onTimeRangeChange={onTimeRangeChange}
                        isPublic={isPublic}
                        hasAnyTests={hasAnyTests}
                        hasCurrentRangeTests={tests.length > 0}
                        serverFilterMode={serverFilterMode}
                        selectedSingleServer={selectedSingleServer}
                        selectedMultipleServers={selectedMultipleServers}
                        onServerFilterModeChange={setServerFilterMode}
                        onSelectedSingleServerChange={setSelectedSingleServer}
                        onSelectedMultipleServersChange={setSelectedMultipleServers}
                        multipleServerDisplayMode={multipleServerDisplayMode}
                        onMultipleServerDisplayModeChange={setMultipleServerDisplayMode}
                      />
                    </SortableItem>
                  );
                } else if (sectionId === "recent" && tests.length > 0) {
                  return (
                    <SortableItem key="recent" id="recent">
                      <DraggableRecentSpeedtests
                        tests={tests}
                        displayedTests={displayedTests}
                        displayCount={displayCount}
                        defaultDisplayCount={recentSpeedtestsRows}
                        setDisplayCount={setDisplayCount}
                        isRecentTestsOpen={isRecentTestsOpen}
                        setIsRecentTestsOpen={setIsRecentTestsOpen}
                        columns={columns}
                        mobileColumns={mobileColumns}
                      />
                    </SortableItem>
                  );
                }
                return null;
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

// Wrapper component for Recent Speedtests with drag handle
interface DraggableRecentSpeedtestsProps {
  tests: SpeedTestResult[];
  displayedTests: SpeedTestResult[];
  displayCount: number;
  defaultDisplayCount: number;
  setDisplayCount: (count: number | ((prev: number) => number)) => void;
  isRecentTestsOpen: boolean;
  setIsRecentTestsOpen: (open: boolean) => void;
  columns: ColumnDef<SpeedTestResult>[];
  mobileColumns: ColumnDef<SpeedTestResult>[];
  dragHandleRef?: (node: HTMLElement | null) => void;
  dragHandleListeners?: Record<string, (...args: unknown[]) => unknown>;
  dragHandleClassName?: string;
}

const DraggableRecentSpeedtests: React.FC<DraggableRecentSpeedtestsProps> = ({
  tests,
  displayedTests,
  displayCount,
  defaultDisplayCount,
  setDisplayCount,
  isRecentTestsOpen,
  setIsRecentTestsOpen,
  columns,
  mobileColumns,
  dragHandleRef,
  dragHandleListeners,
  dragHandleClassName,
}) => {
  return (
    <div className="glass-panel overflow-hidden">
      <Collapsible
        open={isRecentTestsOpen}
        onOpenChange={setIsRecentTestsOpen}
        className="flex flex-col h-full"
      >
        <CollapsibleTrigger
          className={cn(
            "flex justify-between items-center w-full px-4 py-3 sm:py-2 min-h-[44px] sm:min-h-0",
            "text-left transition-all duration-200 touch-manipulation"
          )}
        >
          <div className="flex items-center gap-2">
            <div
              ref={dragHandleRef}
              {...dragHandleListeners}
              className={cn(
                "cursor-grab active:cursor-grabbing touch-none p-1 -m-1",
                dragHandleClassName
              )}
            >
              <FaGripVertical className="w-4 h-4 text-gray-400 dark:text-gray-600" />
            </div>
            <h2 className="text-gray-900 dark:text-white text-lg sm:text-xl font-semibold p-1 select-none">
              Recent Speedtests
            </h2>
          </div>
          <div className="p-1 -m-1">
            <ChevronDownIcon
              className={cn(
                isRecentTestsOpen ? "transform rotate-180" : "",
                "w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform duration-200"
              )}
            />
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              type: "spring",
              stiffness: 300,
              damping: 20,
            }}
            className="px-3 sm:px-4 sm:pt-0 pt-6 pb-6 flex-1"
          >
            {/* Desktop Table View */}
            <div className="hidden md:block">
              <DataTable
                columns={columns}
                data={displayedTests}
                showPagination={false}
                pageSize={Math.max(displayedTests.length, 1)}
                showColumnVisibility={true}
                showRowSelection={false}
                filterColumn="serverName"
                filterPlaceholder="Filter by server..."
                className="-mt-4"
              />
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden">
              <DataTable
                columns={mobileColumns}
                data={displayedTests}
                showPagination={false}
                pageSize={Math.max(displayedTests.length, 1)}
                showColumnVisibility={false}
                showRowSelection={false}
                showHeaders={false}
                className="-mt-4"
                tableClassName="border-0"
              />
            </div>
            {/* Test Count and Load More */}
            {tests.length > defaultDisplayCount && (
              <div className="mt-4 space-y-3">
                {/* Test Count */}
                <div className="text-center">
                  <span className="text-gray-500 dark:text-gray-500 text-xs sm:text-sm">
                    Showing {displayedTests.length} of {tests.length} tests
                  </span>
                </div>

                {/* Load More / Show Less Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
                  {tests.length > displayCount && (
                    <button
                      onClick={() => setDisplayCount((prev) => prev + 5)}
                      className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-3 sm:py-2 min-h-[44px] sm:min-h-0 bg-blue-600/10 hover:bg-blue-600/20 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 rounded-lg transition-colors duration-200 text-sm font-medium touch-manipulation border border-blue-500/20 hover:border-blue-600/50"
                    >
                      Load {Math.min(5, tests.length - displayCount)} more
                      <span className="ml-2">↓</span>
                    </button>
                  )}

                  {displayCount > defaultDisplayCount && (
                    <button
                      onClick={() => setDisplayCount(defaultDisplayCount)}
                      className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-3 sm:py-2 min-h-[44px] sm:min-h-0 bg-gray-600/10 hover:bg-gray-600/20 text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 rounded-lg transition-colors duration-200 text-sm font-medium touch-manipulation"
                    >
                      Show less
                      <span className="ml-2">↑</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
